import {
  PermissionCategory,
  type Permission,
  PermissionType,
} from '@repo/api-gw-sdk';

export type PermissionDescriptor = Permission & {
  isGlobal: boolean;
  title: string;
  group?: string;
};

const permissions: Record<PermissionType, { title: string; group?: string }> = {
  [PermissionType.GlobalSettingsView]: {
    title: 'View global settings',
  },
  [PermissionType.VaultAccountsManage]: {
    title: 'Manage vault accounts',
  },
  [PermissionType.CostExplorerView]: {
    title: 'View cost explorer',
  },
  [PermissionType.UserAccessManage]: {
    title: 'Manage user access',
    group: 'Access management',
  },
  [PermissionType.SamlGroupAccessManage]: {
    title: 'Manage SAML group access',
    group: 'Access management',
  },
  [PermissionType.ApiCredentialsManage]: {
    title: 'Manage API credentials',
    group: 'Access management',
  },
  [PermissionType.ApiCredentialsRotateSecret]: {
    title: 'Rotate API client secret',
    group: 'Access management',
  },
  [PermissionType.CustomRolesManage]: {
    title: 'Manage custom roles',
    group: 'Access management',
  },
  [PermissionType.PermissionIdUnspecified]: {
    title: '',
  },
  [PermissionType.DashboardView]: {
    title: 'View dashboard',
  },
  [PermissionType.InventoryView]: {
    title: 'View inventory',
  },
  [PermissionType.JobsView]: {
    title: 'View jobs',
  },
  [PermissionType.AuditLogsView]: {
    title: 'View audit logs',
  },
  [PermissionType.BackupSettingsView]: {
    title: 'View backup settings',
  },
  [PermissionType.StorageGatewayView]: {
    title: 'View storage gateway',
  },
  [PermissionType.DatabaseExplorerSearch]: {
    title: 'Search for tables and use database explorer',
    group: 'Search and explore',
  },
  [PermissionType.FileExplorerSearch]: {
    title: 'Search for files and use file explorer',
    group: 'Search and explore',
  },
  [PermissionType.SnapshotsTakeOrConvert]: {
    title: 'Take, convert, and hold snapshots',
  },
  [PermissionType.ResourcesRestore]: {
    title: 'Restore resources',
  },
  [PermissionType.ConfigurationValidate]: {
    title: 'Validate required configuration',
  },
  [PermissionType.FilesRestore]: {
    title: 'Restore files',
    group: 'Restore granular data',
  },
  [PermissionType.DatabasesQuery]: {
    title: 'Query databases',
    group: 'Restore granular data',
  },
  [PermissionType.DataClassesOverride]: {
    title: 'Override data classes',
    group: 'Override classifications',
  },
  [PermissionType.EnvironmentsOverride]: {
    title: 'Override environments',
    group: 'Override classifications',
  },
  [PermissionType.DetectedAppsOverride]: {
    title: 'Override detected apps',
    group: 'Override classifications',
  },
  [PermissionType.ControlViolationsMute]: {
    title: 'Mute and unmute violations',
  },
  [PermissionType.BackupExclusionManage]: {
    title: 'Manage backup exclusion',
  },
  [PermissionType.VaultsManage]: {
    title: 'Manage vaults',
    group: 'Manage backup settings',
  },
  [PermissionType.SourceAccountsManage]: {
    title: 'Manage source accounts',
    group: 'Manage backup settings',
  },
  [PermissionType.RestoreAccountsManage]: {
    title: 'Manage restore accounts',
    group: 'Manage backup settings',
  },
  [PermissionType.BackupPoliciesManage]: {
    title: 'Manage backup policies',
    group: 'Manage backup settings',
  },
  [PermissionType.DiscoveryConfigurationManage]: {
    title: 'Manage discovery configuration',
    group: 'Manage backup settings',
  },
  [PermissionType.BackupConfigurationManage]: {
    title: 'Manage backup configuration',
    group: 'Manage backup settings',
  },
  [PermissionType.ControlsManage]: {
    title: 'Manage controls',
    group: 'Manage backup settings',
  },
  [PermissionType.RansomwareDetectionManage]: {
    title: 'Manage ransomware detection',
    group: 'Manage backup settings',
  },
  [PermissionType.NotificationPoliciesManage]: {
    title: 'Manage notification policies',
    group: 'Manage backup settings',
  },
  [PermissionType.StorageGatewayManage]: {
    title: 'Manage storage gateway',
    group: 'Manage storage gateway',
  },
};

export const Permissions = Object.fromEntries(
  Object.entries(permissions).filter(
    ([key]) => key !== PermissionType.PermissionIdUnspecified
  )
);

const permissionCategories: Record<PermissionCategory, { title: string }> = {
  [PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION]: {
    title: 'Administration',
  },
  [PermissionCategory.PERMISSION_CATEGORY_GLOBAL_SETTINGS]: {
    title: 'Global settings',
  },
  [PermissionCategory.PERMISSION_CATEGORY_OPERATION]: {
    title: 'Operation',
  },
  [PermissionCategory.PERMISSION_CATEGORY_VIEWING]: {
    title: 'Viewing',
  },
};

export const PermissionCategories = Object.fromEntries(
  Object.entries(permissionCategories)
);
